import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { UserService, HelperService, EventBusService, resolveLocalizedString } from "@mypxplat/xplat/core";

import { ConnectBaseComponent } from "@mypxplat/xplat/features";
import { WebCommunityService } from "@mypxplat/xplat/web/core";
import { interval, Subject } from "rxjs";
import { delay, skip, takeUntil, throttle } from "rxjs/operators";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-connect",
  templateUrl: "connect.component.html",
  standalone: false,
})
export class ConnectComponent extends ConnectBaseComponent {
  private _skills: Array<any>;
  get skills() {
    return this._skills;
  }

  @Input() set skills(skills) {
    if (skills && skills.length) {
      this._skills = skills;
      this.getUsersBySkills();
      // If browsingSkillId was set before skills loaded, resolve it now
      if (this._browsingSkillId && !this.browsingSkill && this.communityService.skillsMap[this._browsingSkillId]) {
        this.browsingSkill = this.communityService.skillsMap[this._browsingSkillId];
        this.loadSkillMembers();
      }
    }
  }

  private _browsingSkillId: string;
  @Input() set browsingSkillId(skillId: string) {
    this._browsingSkillId = skillId;
    if (skillId && this.communityService.skillsMap[skillId]) {
      this.browsingSkill = this.communityService.skillsMap[skillId];
      this.loadSkillMembers();
    } else {
      this.browsingSkill = null;
      this.skillMembers = [];
    }
  }
  get browsingSkillId() {
    return this._browsingSkillId;
  }

  public browsingSkill: any;
  public skillMembers: Array<any> = [];
  public skillMembersLastDoc: any;
  public skillMembersHasMore: boolean = false;
  public loadingSkillMembers: boolean = false;
  public skillMembersPageSize: number = 50;

  @Output() membersSearched: EventEmitter<any> = new EventEmitter();

  constructor(
    userService: UserService,
    private _eventbusService: EventBusService,
    public communityService: WebCommunityService,
    public router: Router,
    public helperService: HelperService
  ) {
    super(userService);
  }

  ngOnInit() {
    this._queryStream$
      .pipe(
        skip(1),
        throttle(
          () => {
            return interval(1000);
          },
          { leading: true, trailing: true }
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.autocompleteMemberSearch();
      });
    this.communityService.getPostsWithReplies({ mostPopular: true }).then((result) => {
      this.communityService.watchPostsWithReplies();
    });
    this.communityService.getShowcases().subscribe((result) => {
      this.showcases = result;
    });
  }

  autocompleteMemberSearch(limit = 5) {
    if (this.query) {
      // Post-DENG-728 the skill `title` is a per-locale map ({ en, es, ... }), not a bare
      // string, so `item.title.toLowerCase()` threw "de.title.toLowerCase is not a function"
      // on every keystroke here. Resolve the localized string (which also passes legacy
      // string titles through and falls back to string_id) before matching.
      const lang = this.helperService.selectedLanguage?.code || "en";
      const query = this.query.toLowerCase();
      let filteredSkills = this.skills.filter((item) => resolveLocalizedString(item.title, lang, item.string_id).toLowerCase().indexOf(query) > -1);
      let idArray = [];
      filteredSkills.forEach((skill) => idArray.push(skill.string_id));
      while (idArray.length > 10) idArray.pop();
      return this.communityService.findMembers(idArray.length ? idArray : false, this.query.toLowerCase(), this.query.toLowerCase(), limit).then((result) => {
        this.memberResults = result;
        return this.memberResults;
      });
    }
  }

  playAudio(file, event) {
    if (event) event.stopPropagation();
    const button = (event?.target as HTMLElement).closest("button");
    if (button?.hasAttribute("data-focus-restored")) {
      button.removeAttribute("data-focus-restored");
      return;
    }
    if (this.communityService.playingAudioFile && this.communityService.playingAudioFile.id == file.id) {
      this._eventbusService.emit(this._eventbusService.types.playAudio, false);
    } else {
      this._eventbusService.emit(this._eventbusService.types.playAudio, {
        ...file,
        hideDownloadOption: true,
        triggerElement: button,
      });
    }
  }

  getUsersBySkills() {
    let promises = [];
    this.skills.forEach((skill) => {
      promises.push(
        this.communityService.findMembersBySkills([skill.string_id], 100).then((results) => {
          if (results && results.length && results.length > 3) {
            let shuffled = this.helperService.shuffleArray(results);
            this.membersBySkillSample[skill.string_id] = [shuffled[0], shuffled[1], shuffled[2]];
          } else {
            this.membersBySkillSample[skill.string_id] = results;
          }
        })
      );
    });
    Promise.all(promises).then(() => {});
  }

  goToProfile(profile) {
    this.router.navigate(["../profile/", profile.id]);
  }

  search() {
    this.autocompleteMemberSearch(100).then((results) => {
      this.communityService.activeProfileResults = results;
      this.membersSearched.next({
        query: this.query,
        results: results,
      });
    });
  }

  browseSkill(skill) {
    this.router.navigate(["../community/connect", skill.string_id]);
  }

  loadSkillMembers() {
    this.loadingSkillMembers = true;
    this.skillMembers = [];
    this.skillMembersLastDoc = null;
    this.communityService.findMembersBySkillsPaginated([this.browsingSkillId], this.skillMembersPageSize).then((result) => {
      this.skillMembers = result.profiles;
      this.skillMembersLastDoc = result.lastDoc;
      this.skillMembersHasMore = result.hasMore;
      this.loadingSkillMembers = false;
    });
  }

  loadMoreSkillMembers() {
    if (!this.skillMembersHasMore || this.loadingSkillMembers) return;
    this.loadingSkillMembers = true;
    this.communityService.findMembersBySkillsPaginated([this.browsingSkillId], this.skillMembersPageSize, this.skillMembersLastDoc).then((result) => {
      this.skillMembers = [...this.skillMembers, ...result.profiles];
      this.skillMembersLastDoc = result.lastDoc;
      this.skillMembersHasMore = result.hasMore;
      this.loadingSkillMembers = false;
    });
  }

  ngOnDestroy() {
    this.communityService.unwatch();
  }
}
